import type {
	CryptoCompleted,
	CryptoDataDescription,
	CryptoGraphDetail,
} from "@/shared/types/integrations/CoinGecko/CryptoDetail";
import type { CryptoCurrency } from "@/shared/types/integrations/CoinGecko/CryptoCurrency";
import { bus } from "@/shared/events/integrations/coinGeckoEventEmitter";
import { get } from "@/shared/config/server/HttpClient";
import type { SearchCrypto } from "@/shared/types/integrations/CoinGecko/SearchCrypto";
import type { TrendingCryptoCurrency } from "@/shared/types/integrations/CoinGecko/TrendingCryptoCurrency";
import requestResponseTrait from "@/shared/config/error/requestResponseTrait";
import sanitize from "@/shared/utils/sanitize";
import type { ResponseTraitError } from "@/types/Error/Error";

/**
 * @description Handles the request to get the cryptocurrency listing.
 *
 * @param {number} per_page - The limit number of cryptocurrencies to return
 * @param {number} page - The page number of cryptocurrencies to return
 * @returns Promise<CryptoCurrency[] | ResponseTraitError>
 * @throws {Error} If the request to the proxy fails
 */
export async function listCryptoCurrencies(
	per_page = 12,
	page = 1,
): Promise<CryptoCurrency[]> {
	const url: string = `/proxy/cryptos?vs_currency=usd&per_page=${per_page}&page=${page}&precision=2`;

	try {
		const response = await get(url);

		await requestResponseTrait(response);

		const cryptos: CryptoCurrency[] = await sanitize(await response.json());

		bus.emit("getCryptoCurrencies", { cryptos });

		return cryptos;
	} catch (error) {
		throw error;
	}
}

/**
 * @description Handles the request to get the cryptocurrency.
 *
 * @param {string} slug - The slug string
 * @returns Promise<CryptoCurrency | ResponseTraitError>
 * @throws {Error} If the request to the proxy fails
 */
export async function detailCrypto(slug: string): Promise<CryptoCompleted | ResponseTraitError> {
	const from = new Date();
	const to = new Date();

	from.setFullYear(from.getFullYear() - 1);
    to.setDate(to.getDate() + 1);

	let url = `/proxy/cryptos/${slug}/market_chart?vs_currency=usd&from=${from.getTime() / 1000}&to=${to.getTime() / 1000}`;

	try {
		const responseGraphDetail = await get(url);

		await requestResponseTrait(responseGraphDetail);

		const cryptoGraphData: CryptoGraphDetail = await sanitize(await responseGraphDetail.json());

		url = `/proxy/cryptos/${slug}`;

		const cryptoDetailData = await get(url);

		await requestResponseTrait(cryptoDetailData);

		const cryptoDescriptionData: CryptoDataDescription = await sanitize(
			await cryptoDetailData.json(),
		);

		const { default: translate } = await import("@/shared/utils/externalDataTranslator");

		cryptoDescriptionData.description.en = await translate(
			cryptoDescriptionData.description.en,
		);

		if (cryptoDescriptionData?.categories) {
			const categoriesString = cryptoDescriptionData.categories.join(" | ");
			const translatedCategoriesString = await translate(categoriesString);
			cryptoDescriptionData.categories = [ translatedCategoriesString ];
		}

		return {
			description: cryptoDescriptionData,
			prices: cryptoGraphData.prices,
			market_caps: cryptoGraphData.market_caps,
			total_volumes: cryptoGraphData.total_volumes,
		};
	} catch (error) {
		return error as ResponseTraitError;
	}
}

/**
 * @description Handles the request to get the cryptocurrency based on the query.
 *
 * @param {string} query - The query string
 * @returns Promise<SearchCrypto[] | ResponseTraitError>
 * @throws {Error} If the request to the proxy fails
 */
export async function searchCryptos(query: string): Promise<SearchCrypto[] | ResponseTraitError> {
	const url = `/proxy/cryptos/search?query=${query}`;

	try {
		const response = await get(url);

		await requestResponseTrait(response);

		const cryptos: SearchCrypto[] = await sanitize(await response.json());

		return cryptos.slice(0, 7);
	} catch (error) {
		return error as ResponseTraitError;
	}
}

type GetTrendingCryptosResponse = [TrendingCryptoCurrency];

/**
 * @description Handles the request to get the trending cryptocurrencies.
 *
 * @returns Promise<GetTrendingCryptosResponse | ResponseTraitError>
 * @throws {Error} If the request to the proxy fails
 */
export async function getTrendingCryptos(): Promise<
	GetTrendingCryptosResponse | ResponseTraitError
> {
	try {
		const response = await get("/proxy/cryptos/trending");

		await requestResponseTrait(response);

		const cryptos: GetTrendingCryptosResponse = await sanitize(await response.json());

		bus.emit("getTrendingCrypto", {
			cryptos: cryptos,
		});
		return cryptos;
	} catch (error) {
		return error as ResponseTraitError;
	}
}
